import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  IconButton,
  Avatar,
  List,
  ListItemButton,
  ListItemAvatar,
  ListItemText,
  Divider,
  Badge,
  useTheme,
  useMediaQuery,
  CircularProgress,
  InputBase,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Send as SendIcon,
  ArrowBack as ArrowBackIcon,
  Search as SearchIcon,
  Forum as ForumIcon,
  DoneAll as DoneAllIcon,
  AttachFile as AttachFileIcon,
  EmojiEmotions as EmojiIcon,
} from '@mui/icons-material';
import { useLanguage } from '../context/LanguageContext';
import { contactsAPI, contactChatsAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';
import { useNotifications } from '../context/NotificationContext';
import { useSearchParams } from 'react-router-dom';

/* GetSettled Theme Colors */
const GS = {
  blue: '#FF8C42',
  blueLight: '#FFF5EF',
  blueDark: '#E06526',
  orange: '#FF8C42',
  orangeLight: '#FFF5EF',
  orangeDark: '#E06526',
  amber: '#F0A500',
  teal: '#4A7C8E',
  tealLight: '#E6F2F5',
  tealDark: '#1E3A45',
  textPrimary: '#1E3A45',
  textMuted: '#6B7280',
  border: '#E5E7EB',
  surface: '#F9FAFB',
  white: '#ffffff',
};

// Fallback high-quality Mock Contacts (German-focused names/services for consistency)
const MOCK_CONTACTS = [
  {
    id: 'mock-1',
    name: 'Emma Weber',
    service: 'Hausreinigung (House Cleaning)',
    avatarColor: '#4A7C8E',
    unread: true,
    lastMessageText: 'Hallo! Steht der Termin morgen noch?',
    lastMessageTime: '10:30',
  },
  {
    id: 'mock-2',
    name: 'Lukas Schmidt',
    service: 'Gartengestaltung (Garden Design)',
    avatarColor: '#FF8C42',
    unread: false,
    lastMessageText: 'Können wir den Preis nochmal besprechen?',
    lastMessageTime: 'Gestern',
  },
  {
    id: 'mock-3',
    name: 'Mia Wagner',
    service: 'Elektroinstallation (Electrical)',
    avatarColor: '#F0A500',
    unread: false,
    lastMessageText: 'Vielen Dank für die schnelle Hilfe!',
    lastMessageTime: '12 Jun',
  },
  {
    id: 'mock-4',
    name: 'Jonas Müller',
    service: 'Heizung & Sanitär (Plumbing)',
    avatarColor: '#8E4A7C',
    unread: false,
    lastMessageText: 'Ich sende Ihnen gleich die Fotos.',
    lastMessageTime: '10 Jun',
  },
];

// Fallback high-quality Mock Messages
const MOCK_MESSAGES = {
  'mock-1': [
    { id: 1, text: 'Hallo, ich wollte fragen, ob der Termin für morgen um 9:00 Uhr noch steht?', sender: 'them', time: '10:28' },
    { id: 2, text: 'Ja, der Termin ist bestätigt. Ich bin pünktlich da!', sender: 'me', time: '10:29' },
    { id: 3, text: 'Super, danke! Bis morgen.', sender: 'them', time: '10:30' },
  ],
  'mock-2': [
    { id: 1, text: 'Hallo Lukas, ich habe mir Ihren Garten angesehen und der Kostenvoranschlag ist fertig.', sender: 'me', time: 'Gestern 14:15' },
    { id: 2, text: 'Können wir den Preis nochmal besprechen? Ich finde es ein bisschen zu hoch.', sender: 'them', time: 'Gestern 14:30' },
  ],
  'mock-3': [
    { id: 1, text: 'Guten Tag, das Ersatzteil ist da. Ich kann heute Nachmittag vorbeikommen.', sender: 'me', time: '12 Jun 10:00' },
    { id: 2, text: 'Das wäre perfekt, danke!', sender: 'them', time: '12 Jun 10:15' },
    { id: 3, text: 'Ich bin jetzt fertig. Alles funktioniert wieder einwandfrei.', sender: 'me', time: '12 Jun 11:30' },
    { id: 4, text: 'Vielen Dank für die schnelle Hilfe!', sender: 'them', time: '12 Jun 11:45' },
  ],
  'mock-4': [
    { id: 1, text: 'Können Sie mir Fotos von dem defekten Ventil schicken?', sender: 'me', time: '10 Jun 09:12' },
    { id: 2, text: 'Ich sende Ihnen gleich die Fotos.', sender: 'them', time: '10 Jun 09:15' },
  ],
};

const Chat = () => {
  const { t, locale } = useLanguage();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { user } = useAuth();
  const { refresh: refreshNotifications } = useNotifications();
  const [searchParams, setSearchParams] = useSearchParams();
  const urlChatId = searchParams.get('chatId');
  const urlContactId = searchParams.get('contactId');

  // Contact Selection states (from contactsAPI)
  const [loadingContacts, setLoadingContacts] = useState(true);
  const [dropdownContacts, setDropdownContacts] = useState([]);
  const [selectedContactId, setSelectedContactId] = useState('all');

  // Conversation list states (from contactChatsAPI)
  const [loadingConversations, setLoadingConversations] = useState(false);
  const [conversations, setConversations] = useState([]);
  const [selectedConversationId, setSelectedConversationId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Messages states (from contactChatsAPI)
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);

  const messageContainerRef = useRef(null);
  const lastConversationIdRef = useRef(null);
  const lastMessagesLengthRef = useRef(0);

  // Scroll to the bottom of the message container without scrolling the main window/screen
  const scrollToBottom = useCallback((behavior = 'auto') => {
    if (messageContainerRef.current) {
      messageContainerRef.current.scrollTo({
        top: messageContainerRef.current.scrollHeight,
        behavior: behavior,
      });
    }
  }, []);

  // Fetch all contacts on page load to populate the dropdown
  useEffect(() => {
    const fetchContacts = async () => {
      setLoadingContacts(true);
      try {
        const response = await contactsAPI.getContacts('', '', 0, 200);
        if (response?.data?.success) {
          const list = response.data.data || [];
          setDropdownContacts(list);
          setSelectedContactId('all');
        }
      } catch (err) {
        console.error('Failed to load contacts for dropdown:', err);
      } finally {
        setLoadingContacts(false);
      }
    };
    fetchContacts();
  }, []);

  // Helper to extract an array safely from any API response
  const extractArray = (data) => {
    if (!data) return [];
    if (Array.isArray(data)) return data;

    // Check common nested structures
    if (data.messages && Array.isArray(data.messages)) return data.messages;
    if (data.conversations && Array.isArray(data.conversations)) return data.conversations;

    if (data.data && Array.isArray(data.data)) return data.data;
    if (data.data && data.data.messages && Array.isArray(data.data.messages)) return data.data.messages;
    if (data.data && data.data.conversations && Array.isArray(data.data.conversations)) return data.data.conversations;
    if (data.data && data.data.items && Array.isArray(data.data.items)) return data.data.items;

    if (data.payload && Array.isArray(data.payload)) return data.payload;
    if (data.payload && data.payload.messages && Array.isArray(data.payload.messages)) return data.payload.messages;
    if (data.payload && data.payload.conversations && Array.isArray(data.payload.conversations)) return data.payload.conversations;
    if (data.payload && data.payload.items && Array.isArray(data.payload.items)) return data.payload.items;
    if (data.payload && data.payload.data && Array.isArray(data.payload.data)) return data.payload.data;

    if (data.items && Array.isArray(data.items)) return data.items;
    return [];
  };

  // Fetch conversations for the selected contact
  const fetchConversations = useCallback(async (showLoading = true) => {
    if (!selectedContactId) {
      setConversations([]);
      return;
    }
    if (showLoading) setLoadingConversations(true);
    try {
      const contactIdParam = selectedContactId === 'all' ? null : selectedContactId;
      const response = await contactChatsAPI.getConversations(contactIdParam, searchQuery);
      const list = extractArray(response?.data);
      setConversations(list);
    } catch (err) {
      console.error('Failed to load conversations:', err);
      setConversations([]);
    } finally {
      if (showLoading) setLoadingConversations(false);
    }
  }, [selectedContactId, searchQuery]);

  useEffect(() => {
    fetchConversations(true);
  }, [fetchConversations]);

  // Poll conversations and messages every 5 seconds (like WhatsApp)
  useEffect(() => {
    if (!selectedContactId) return;

    const interval = setInterval(() => {
      fetchConversations(false);

      if (selectedConversationId) {
        contactChatsAPI.getMessages(selectedConversationId)
          .then((response) => {
            const msgList = extractArray(response?.data);
            setMessages((prev) => {
              const pendingMsgs = prev.filter((m) => m.isPending);
              const activePendingMsgs = pendingMsgs.filter((pm) => {
                const pmText = pm.message || pm.text || pm.content || pm.body || pm.messageText || pm.msg;
                return !msgList.some((m) => {
                  const mText = m.message || m.text || m.content || m.body || m.messageText || m.msg;
                  return mText === pmText;
                });
              });
              const mergedList = [...msgList, ...activePendingMsgs];

              if (mergedList.length > prev.length) {
                // Mark as read on backend if new messages arrived in current active chat
                contactChatsAPI.markAsRead(selectedConversationId, null)
                  .then(() => refreshNotifications(true))
                  .catch((err) => console.error('Poll markAsRead error:', err));
              }
              return mergedList;
            });
          })
          .catch((err) => console.error('Poll messages error:', err));
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [selectedContactId, selectedConversationId, fetchConversations]);



  // Find currently selected conversation details
  const selectedConversation = useMemo(() => {
    const list = Array.isArray(conversations) ? conversations : [];
    return list.find(
      (c) => (c.id === selectedConversationId || c.conversationId === selectedConversationId || c.chatId === selectedConversationId)
    );
  }, [conversations, selectedConversationId]);

  // Scroll to bottom when loading finishes (switching chats) or when new messages arrive
  useEffect(() => {
    if (!selectedConversationId) {
      lastConversationIdRef.current = null;
      lastMessagesLengthRef.current = 0;
      return;
    }

    if (loadingMessages) return;

    if (Array.isArray(messages) && messages.length > 0) {
      // If we just finished loading a new conversation
      if (lastConversationIdRef.current !== selectedConversationId) {
        const timer = setTimeout(() => {
          scrollToBottom('auto');
        }, 50);
        lastConversationIdRef.current = selectedConversationId;
        lastMessagesLengthRef.current = messages.length;
        return () => clearTimeout(timer);
      }
      // If a new message was added to the current conversation
      else if (messages.length > lastMessagesLengthRef.current) {
        const timer = setTimeout(() => {
          scrollToBottom('smooth');
        }, 50);
        lastMessagesLengthRef.current = messages.length;
        return () => clearTimeout(timer);
      }
    }
  }, [messages, loadingMessages, selectedConversationId, scrollToBottom]);

  // Select a conversation and load messages
  const handleSelectConversation = async (conversationId) => {
    toast.dismiss(conversationId);
    setSelectedConversationId(conversationId);
    setLoadingMessages(true);
    try {
      // 1. Fetch messages
      const response = await contactChatsAPI.getMessages(conversationId);
      const msgList = extractArray(response?.data);
      setMessages(msgList);

      // 2. Mark as read on the backend (passing lastReadMessageId: null to read all)
      await contactChatsAPI.markAsRead(conversationId, null);
      refreshNotifications(true);

      // 3. Clear unread count locally for instant UI update
      setConversations((prev) => {
        const list = Array.isArray(prev) ? prev : [];
        return list.map((c) =>
          (c.id === conversationId || c.conversationId === conversationId || c.chatId === conversationId)
            ? { ...c, unreadCount: 0 }
            : c
        );
      });
    } catch (err) {
      console.error('Failed to load conversation messages:', err);
    } finally {
      setLoadingMessages(false);
    }
  };

  const handleBack = () => {
    setSelectedConversationId(null);
    setSearchParams({});
  };

  // Handle URL query parameters (e.g. from notification clicks)
  useEffect(() => {
    if (urlContactId) {
      setSelectedContactId(urlContactId);
    }
    if (urlChatId) {
      handleSelectConversation(urlChatId);
    }
  }, [urlChatId, urlContactId]);

  // Send message (reply) handler
  const handleSendMessage = async () => {
    if (!inputValue.trim() || !selectedConversationId || sendingMessage) return;

    const textToSend = inputValue.trim();
    setInputValue('');
    setSendingMessage(true);

    const optimisticId = `temp-${Date.now()}`;
    const optimisticMsg = {
      id: optimisticId,
      message: textToSend,
      sender: 'me',
      senderRole: 'Provider',
      senderType: 'Provider',
      sentByProvider: true,
      isIncoming: false,
      createdAtUtc: new Date().toISOString(),
      createdAt: new Date().toISOString(),
      time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      isPending: true
    };

    // Add message optimistically
    setMessages((prev) => [...prev, optimisticMsg]);

    try {
      const response = await contactChatsAPI.sendMessage(selectedConversationId, textToSend);

      if (response?.data?.message === "This contact is inactive.") {
        toast.error(response.data.message);
        setMessages((prev) => prev.filter((m) => m.id !== optimisticId));
        setInputValue(textToSend);
        setSendingMessage(false);
        return;
      }

      const sentMsg = response?.data?.data || response?.data?.payload || response?.data;

      if (sentMsg) {
        const markedMsg = {
          ...sentMsg,
          sender: 'me',
          senderRole: 'Provider',
          senderType: 'Provider',
          sentByProvider: true,
          isIncoming: false
        };
        // Replace the optimistic message with the actual message from the backend
        setMessages((prev) =>
          prev.map((m) => (m.id === optimisticId ? markedMsg : m))
        );
      } else {
        // Fallback: remove the optimistic message and fetch messages again
        setMessages((prev) => prev.filter((m) => m.id !== optimisticId));
        const resMsg = await contactChatsAPI.getMessages(selectedConversationId);
        setMessages(extractArray(resMsg?.data));
      }

      // Fetch conversations to update the lastMessage text/time immediately
      fetchConversations(false);
    } catch (err) {
      console.error('Failed to send message:', err);
      // Remove the optimistic message and restore text to input if failed
      setMessages((prev) => prev.filter((m) => m.id !== optimisticId));
      setInputValue(textToSend);

      if (err.response?.data?.message === "This contact is inactive.") {
        toast.error(err.response.data.message);
      }
    } finally {
      setSendingMessage(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Format timestamp nicely
  const formatTime = (timeString) => {
    if (!timeString) return '';
    try {
      const date = new Date(timeString);
      if (!isNaN(date.getTime())) {
        return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      }
    } catch (e) { }
    return timeString;
  };


  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: 1400,
        mx: 'auto',
        p: { xs: 1, sm: 2, md: 3 },
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <style>
        {`
          body {
            overflow: hidden !important;
          }
          main {
            height: calc(100vh - 80px) !important;
            min-height: 0 !important;
            overflow: hidden !important;
          }
          @media (max-width: 600px) {
            main {
              height: calc(100vh - 64px) !important;
            }
          }
        `}
      </style>
      {/* Page Header */}
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          mb: 4,
          p: { xs: 2, sm: 4 },
          borderRadius: { xs: 2, sm: 4 },
          background: 'linear-gradient(135deg, #ffffff 0%, #fafafa 100%)',
          border: '1px solid rgba(255, 140, 66, 0.2)',
          boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
          flexShrink: 0,
        }}
      >
        <Box sx={{ width: '100%' }}>
          <Typography
            variant={isMobile ? 'h5' : 'h4'}
            sx={{ fontWeight: 800, letterSpacing: '-0.03em', color: '#313132' }}
          >
            {t('pages.chat.title')}
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mt: 1, fontWeight: 500 }}
          >
            {t('pages.chat.subtitle')}
          </Typography>
        </Box>
      </Box>

      {/* Main Chat Box Container */}
      <Paper
        elevation={0}
        sx={{
          flex: 1,
          display: 'flex',
          overflow: 'hidden',
          borderRadius: 3,
          border: `1px solid ${GS.border}`,
          bgcolor: GS.white,
          boxShadow: '0 2px 12px rgba(0, 0, 0, 0.03)',
        }}
      >
        <Grid container sx={{ height: '100%' }}>
          {(!isMobile || !selectedConversationId) && (
            <Grid
              item
              xs={12}
              md={4}
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                borderRight: { md: `1px solid ${GS.border}` },
                bgcolor: GS.surface,
                overflow: 'hidden',
                minHeight: 0,
              }}
            >
              {/* Sidebar Header: Select Service & Search Bar */}
              <Box sx={{ p: 2, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {/* Select Service Dropdown */}
                <FormControl fullWidth size="small">
                  <InputLabel id="contact-select-label" sx={{ color: GS.textMuted }}>
                    {t('pages.chat.selectContact') || 'Select Service'}
                  </InputLabel>
                  <Select
                    labelId="contact-select-label"
                    id="contact-select"
                    value={selectedContactId || ''}
                    label={t('pages.chat.selectContact') || 'Select Service'}
                    onChange={(e) => {
                      setSelectedContactId(e.target.value);
                      setSelectedConversationId(null);
                      setMessages([]);
                    }}
                    disabled={loadingContacts}
                    sx={{
                      borderRadius: '12px',
                      bgcolor: GS.white,
                      color: GS.textPrimary,
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: GS.border,
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: GS.blue,
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: GS.blue,
                      },
                    }}
                  >
                    <MenuItem value="all">
                      {t('pages.chat.allServices') || 'All Services'}
                    </MenuItem>
                    {loadingContacts ? (
                      <MenuItem value="" disabled>
                        <CircularProgress size={20} sx={{ mr: 1, color: GS.blue }} />
                        {t('pages.chat.loadingContacts') || 'Loading contacts...'}
                      </MenuItem>
                    ) : (
                      dropdownContacts.map((contact) => (
                        <MenuItem key={contact.id} value={contact.id}>
                          {contact.title}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                </FormControl>

                {/* Search Bar */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    bgcolor: GS.white,
                    px: 1.5,
                    py: 1,
                    borderRadius: '12px',
                    border: `1px solid ${GS.border}`,
                    transition: 'border-color 0.2s',
                    '&:focus-within': {
                      borderColor: GS.blue,
                    },
                  }}
                >
                  <SearchIcon sx={{ color: GS.textMuted, mr: 1, fontSize: 20 }} />
                  <InputBase
                    placeholder={t('pages.chat.search')}
                    fullWidth
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    sx={{ fontSize: '0.875rem', color: GS.textPrimary }}
                  />
                </Box>
              </Box>

              <Divider />

              {/* Conversations List */}
              <Box sx={{ flex: 1, overflowY: 'auto', px: 1, py: 1 }}>
                {loadingConversations ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress size={28} sx={{ color: GS.blue }} />
                  </Box>
                ) : conversations.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 6, px: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      {t('pages.chat.noConversations') || 'No users found'}
                    </Typography>
                  </Box>
                ) : (
                  <List sx={{ p: 0 }}>
                    {conversations.map((conv) => {
                      const convId = conv.id || conv.conversationId || conv.chatId;
                      const isSelected = selectedConversationId === convId;
                      const hasUnread = conv.unreadCount > 0 && !isSelected;
                      return (
                        <ListItemButton
                          key={convId}
                          onClick={() => handleSelectConversation(convId)}
                          selected={isSelected}
                          sx={{
                            borderRadius: '12px',
                            mb: 0.5,
                            px: 1.5,
                            py: 1.25,
                            backgroundColor: isSelected ? GS.blueLight : 'transparent',
                            borderLeft: isSelected ? `4px solid ${GS.blue}` : '4px solid transparent',
                            '&:hover': {
                              backgroundColor: isSelected ? GS.blueLight : 'rgba(0, 0, 0, 0.02)',
                            },
                            transition: 'all 0.15s ease',
                          }}
                        >
                          <ListItemAvatar sx={{ minWidth: 48 }}>
                            <Avatar
                              src={conv.contactImageUrl || conv.logoPicture || conv.profileImageUrl || ''}
                              sx={{
                                bgcolor: GS.teal,
                                width: 38,
                                height: 38,
                                fontWeight: 700,
                                fontSize: '0.9rem',
                              }}
                            >
                              {(conv.chatTitle || conv.name || 'C').charAt(0).toUpperCase()}
                            </Avatar>
                          </ListItemAvatar>
                          <ListItemText
                            primary={
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                                <Typography
                                  variant="subtitle2"
                                  sx={{
                                    fontWeight: hasUnread ? 800 : 600,
                                    color: GS.textPrimary,
                                    fontSize: '0.875rem',
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    maxWidth: '70%',
                                  }}
                                >
                                  {conv.chatTitle || conv.name || 'Chat'}
                                </Typography>
                                <Typography variant="caption" sx={{ color: GS.textMuted, fontSize: '0.75rem' }}>
                                  {formatTime(conv.lastMessageTime || conv.updatedAt || conv.createdAt)}
                                </Typography>
                              </Box>
                            }
                            secondary={
                              <Box sx={{ mt: 0.25, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    color: hasUnread ? GS.textPrimary : GS.textMuted,
                                    fontWeight: hasUnread ? 600 : 400,
                                    fontSize: '0.8125rem',
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    maxWidth: '85%',
                                  }}
                                >
                                  {conv.lastMessage || conv.lastMessageText || conv.lastMessageContent || conv.lastMessageBody || 'No messages'}
                                </Typography>
                                {hasUnread && (
                                  <Box
                                    sx={{
                                      bgcolor: '#25D366',
                                      color: '#ffffff',
                                      borderRadius: '50%',
                                      minWidth: 18,
                                      height: 18,
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      fontSize: '0.7rem',
                                      fontWeight: 'bold',
                                      px: 0.5,
                                    }}
                                  >
                                    {conv.unreadCount}
                                  </Box>
                                )}
                              </Box>
                            }
                            disableTypography
                          />
                        </ListItemButton>
                      );
                    })}
                  </List>
                )}
              </Box>
            </Grid>
          )}

          {/* Active Chat Pane */}
          {(!isMobile || selectedConversationId) && (
            <Grid
              item
              xs={12}
              md={8}
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                bgcolor: GS.white,
                overflow: 'hidden',
                minHeight: 0,
              }}
            >
              {selectedConversation ? (
                <>
                  {/* Chat Pane Header */}
                  <Box
                    sx={{
                      p: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      borderBottom: `1px solid ${GS.border}`,
                      flexShrink: 0,
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
                      <IconButton
                        edge="start"
                        onClick={handleBack}
                        sx={{ mr: 1, color: GS.textPrimary }}
                      >
                        <ArrowBackIcon />
                      </IconButton>
                      <Avatar
                        src={selectedConversation.contactImageUrl || selectedConversation.logoPicture || selectedConversation.profileImageUrl || ''}
                        sx={{
                          bgcolor: GS.teal,
                          width: 40,
                          height: 40,
                          fontWeight: 700,
                          mr: 1.5,
                        }}
                      >
                        {(selectedConversation.chatTitle || selectedConversation.name || 'C').charAt(0).toUpperCase()}
                      </Avatar>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: GS.textPrimary, lineHeight: 1.2 }}>
                          {selectedConversation.chatTitle || selectedConversation.name || 'Chat'}
                        </Typography>
                        {selectedConversation.lastMessageTime && (
                          <Typography
                            variant="caption"
                            sx={{
                              color: GS.textMuted,
                              display: 'block',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}
                          >
                            {formatTime(selectedConversation.lastMessageTime || selectedConversation.updatedAt || selectedConversation.createdAt)}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </Box>

                  {/* Messages Stream */}
                  <Box
                    ref={messageContainerRef}
                    sx={{
                      flex: 1,
                      overflowY: 'auto',
                      p: { xs: 2, sm: 3 },
                      bgcolor: '#fafafa',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1.5,
                    }}
                  >
                    {loadingMessages ? (
                      <Box sx={{ display: 'flex', flex: 1, justifyContent: 'center', alignItems: 'center', py: 4 }}>
                        <CircularProgress size={32} sx={{ color: GS.blue }} />
                      </Box>
                    ) : messages.length === 0 ? (
                      <Box sx={{ display: 'flex', flex: 1, flexDirection: 'column', justifyContent: 'center', alignItems: 'center', py: 4 }}>
                        <Typography variant="body2" color="text.secondary">
                          No messages yet. Send a message to start the conversation!
                        </Typography>
                      </Box>
                    ) : (
                      messages.map((msg) => {
                        const isMe =
                          msg.sender === 'me' ||
                          msg.senderRole === 'Provider' ||
                          msg.senderType === 'Provider' ||
                          msg.sentByProvider === true ||
                          msg.isIncoming === false ||
                          (msg.senderRole !== 'AppUser' && msg.senderType !== 'AppUser' && (
                            (user && (
                              msg.senderId === user.id ||
                              msg.senderId === user.userId ||
                              msg.senderId === user.appUserId ||
                              msg.userId === user.id ||
                              msg.userId === user.userId ||
                              msg.userId === user.appUserId ||
                              msg.createdBy === user.id ||
                              msg.createdBy === user.userId
                            )) ||
                            msg.sender === 'Provider'
                          ));
                        return (
                          <Box
                            key={msg.id}
                            sx={{
                              display: 'flex',
                              justifyContent: isMe ? 'flex-end' : 'flex-start',
                              mb: 0.5,
                            }}
                          >
                            <Box
                              sx={{
                                maxWidth: { xs: '85%', sm: '70%' },
                                position: 'relative',
                              }}
                            >
                              <Box
                                sx={{
                                  p: 1.5,
                                  borderRadius: isMe ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                                  bgcolor: isMe ? GS.blue : GS.white,
                                  color: isMe ? GS.white : GS.textPrimary,
                                  boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
                                  border: isMe ? 'none' : `1px solid ${GS.border}`,
                                  overflowWrap: 'break-word',
                                }}
                              >
                                <Typography variant="body2" sx={{ fontSize: '0.875rem', lineHeight: 1.4 }}>
                                  {msg.message || msg.text || msg.content || msg.body || msg.messageText || msg.msg || ''}
                                </Typography>
                              </Box>
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: isMe ? 'flex-end' : 'flex-start',
                                  gap: 0.5,
                                  mt: 0.5,
                                  px: 0.5,
                                }}
                              >
                                <Typography variant="caption" sx={{ color: GS.textMuted, fontSize: '0.7rem' }}>
                                  {formatTime(msg.createdAtUtc || msg.time || msg.createdAt || msg.updatedAt)}
                                </Typography>
                              </Box>
                            </Box>
                          </Box>
                        );
                      })
                    )}
                  </Box>

                  {/* Input Actions Bar */}
                  <Box
                    sx={{
                      p: 2,
                      borderTop: `1px solid ${GS.border}`,
                      bgcolor: GS.white,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      flexShrink: 0,
                    }}
                  >


                    <Box
                      sx={{
                        flex: 1,
                        bgcolor: GS.surface,
                        border: `1px solid ${GS.border}`,
                        borderRadius: '24px',
                        px: 2,
                        py: 0.75,
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      <InputBase
                        placeholder={t('pages.chat.typeMessage')}
                        fullWidth
                        value={inputValue}
                        onChange={(e) => setInputValue(e.target.value)}
                        onKeyPress={handleKeyPress}
                        sx={{ fontSize: '0.875rem', color: GS.textPrimary }}
                      />
                    </Box>

                    <IconButton
                      color="warning"
                      onClick={handleSendMessage}
                      disabled={!inputValue.trim() || sendingMessage}
                      sx={{
                        bgcolor: GS.blue,
                        color: `${GS.white} !important`,
                        '&:hover': {
                          bgcolor: GS.blueDark,
                        },
                        '&.Mui-disabled': {
                          bgcolor: GS.blue,
                          color: `${GS.white} !important`,
                          opacity: 0.6,
                        },
                        transition: 'all 0.2s',
                        p: 1.25,
                      }}
                    >
                      {sendingMessage ? (
                        <CircularProgress size={18} sx={{ color: GS.white }} />
                      ) : (
                        <SendIcon fontSize="small" />
                      )}
                    </IconButton>
                  </Box>
                </>
              ) : (
                /* No selected conversation blank slate */
                <Box
                  sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    p: 4,
                    bgcolor: GS.surface,
                    textAlign: 'center',
                  }}
                >
                  <Avatar
                    sx={{
                      bgcolor: GS.blueLight,
                      color: GS.blue,
                      width: 72,
                      height: 72,
                      mb: 2,
                    }}
                  >
                    <ForumIcon sx={{ fontSize: 36 }} />
                  </Avatar>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: GS.textPrimary, mb: 1 }}>
                    {t('pages.chat.title')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 320 }}>
                    {t('pages.chat.noSelect')}
                  </Typography>
                </Box>
              )}
            </Grid>
          )}
        </Grid>
      </Paper>
    </Box>
  );
};

export default Chat;
